import { AnimationsBehavior, MasterPageDefinition, Style, ThemeFontMap } from '@wix/thunderbolt-becky-types'
import { createCompNode, envRefs, isRefArrayViewItem, qaRefs, themeRefs, Unref } from '@wix/thunderbolt-catharsis'
import { CompInfo, PageTransition } from '@wix/thunderbolt-components-native'
import { refApi as thunderboltRefsAPI } from '@wix/thunderbolt-refs'
import { resolveSpxAttribute } from '@wix/thunderbolt-commons'
import { fontObjectToString, parseFontStr } from '@wix/thunderbolt-becky-root'
import { previewCompInfoNodes } from './previewCompInfoResolvers'

const MOBILE_SITE_WIDTH: number = 320
const DEFAULT_DESKTOP_SITE_WIDTH: number = 980

const compId = createCompNode({
	getDependencies: () => {},
	toViewItem: (component) => component.id,
})

const structure = createCompNode({
	getDependencies: () => {},
	toViewItem: (component) => component,
})

const compLayout = createCompNode({
	getDependencies: () => {},
	toViewItem: (component) => component.layout,
})

const refApi = createCompNode({
	getDependencies: () => {},
	toViewItem: () => thunderboltRefsAPI,
})

const hasResponsiveLayout = createCompNode({
	getDependencies: (component, refs) => refs.layoutQuery(component.layoutQuery!),
	toViewItem: (component, data) => {
		return data !== undefined
	},
})

const compStylableClass = createCompNode({
	getDependencies: () => {},
	toViewItem: (component) => `${component.styleId}__root`,
})

const customClassNames = createCompNode({
	getDependencies: (component, refs) => refs.classnamesQuery(component.classnamesQuery!),
	toViewItem: (__, data) => data?.classnames ?? [],
})

const isMasterPage = createCompNode({
	getDependencies: () => {},
	toViewItem: (component) => component.metaData.pageId === 'masterPage',
})

const compData = createCompNode({
	getDependencies: (component, refs) => refs.dataQuery(component.dataQuery!),
	toViewItem: (__, data) => data,
})

const compDesign = createCompNode({
	getDependencies: (component, refs) => refs.designQuery(component.designQuery!),
	toViewItem: (__, data) => data,
})

const EMPTY_PROPS = {}
const compProps = createCompNode({
	getDependencies: (component, refs) => refs.propertyQuery(component.propertyQuery!),
	toViewItem: (__, data) => data ?? EMPTY_PROPS,
})

const EMPTY_ANIMATIONS: Array<AnimationsBehavior> = []
const animations = createCompNode({
	getDependencies: (component, refs) => refs.behaviorQuery(component.behaviorQuery!),
	toViewItem: (__, data) => data?.animations ?? EMPTY_ANIMATIONS,
})

const siteWidth = createCompNode({
	getDependencies: () => ({
		siteWidth: envRefs.siteWidth,
		viewMode: envRefs.viewMode,
	}),
	toViewItem: (__, data) => {
		if (data.viewMode === 'mobile') {
			return MOBILE_SITE_WIDTH
		}

		return data.siteWidth ?? DEFAULT_DESKTOP_SITE_WIDTH
	},
})

const skin = createCompNode({
	getDependencies: (component, refs) => refs.styleId(component.styleId!),
	toViewItem: (component, data) => {
		return data && 'skin' in data && data.skin ? data.skin : component.skin
	},
})

const mainPageId = createCompNode({
	getDependencies: (__, refs) => refs.dataQuery<MasterPageDefinition['data']>('masterPage'),
	toViewItem: (__, data) => {
		return data.mainPageId || data.mainPage?.id || data.id
	},
})

const globalImageQuality = createCompNode({
	getDependencies: (__, refs) => refs.dataQuery('IMAGE_QUALITY'),
	toViewItem: (__, data) => data as CompInfo['globalImageQuality'],
})

const envCompNode = <Key extends keyof typeof envRefs>(key: Key) =>
	createCompNode<typeof envRefs[Key], Unref<typeof envRefs[Key]>, any>({
		getDependencies: () => envRefs[key],
		toViewItem: (__, data: any) => data,
	})

const deviceType = envCompNode('deviceType')
const experiments = envCompNode('experiments')
const externalBaseUrl = envCompNode('externalBaseUrl')
const isMobileView = envCompNode('isMobileView')
const isConsentPolicyActive = envCompNode('isConsentPolicyActive')
const isTrackClicksAnalyticsEnabled = envCompNode('isTrackClicksAnalyticsEnabled')
const isNonAdaptiveMobile = envCompNode('isNonAdaptiveMobile')
const isQaMode = envCompNode('qaMode')
const isInSeo = envCompNode('isInSeo')
const language = envCompNode('language')
const metaSiteId = envCompNode('metaSiteId')
const topology = envCompNode('publicTopology')
const viewMode = envCompNode('viewMode')
const editorName = envCompNode('editorName')
const formFactor = envCompNode('formFactor')

const siteColors = createCompNode({
	getDependencies: () => themeRefs.siteColors,
	toViewItem: (__, data) => data,
})

const siteFonts = createCompNode({
	getDependencies: () => themeRefs.siteFonts,
	toViewItem: (__, data) => {
		if (!data) {
			return {}
		}

		return data.reduce((acc, font, index) => {
			acc[`font_${index}`] = font
			return acc
		}, {} as ThemeFontMap)
	},
})

const styleProperties = createCompNode({
	getDependencies: (component, refs) => refs.styleId(component.styleId!),
	toViewItem: (__, data) => {
		if (!data) {
			return {}
		}

		if (isRefArrayViewItem<Style>(data)) {
			const defaultStyle = data.values.find((item) => 'style' in item) as Style | undefined
			if (!defaultStyle) {
				return {}
			}
			return defaultStyle.style.properties
		}

		return data.style.properties
	},
})

const direction = createCompNode({
	getDependencies: () => envRefs.language,
	toViewItem: (__, data) => (data === 'he' || data === 'ar' ? 'rtl' : 'ltr'),
})

const isConnectedToDataBinding = createCompNode({
	getDependencies: (component, refs) => refs.connectionQuery(component.connectionQuery!),
	toViewItem: (__, data) => {
		if (!data?.controllerConnections) {
			return false
		}

		return data.controllerConnections.some((item) => item.applicationId === 'dataBinding')
	},
})

const notImplemented = createCompNode({
	getDependencies: () => {},
	toViewItem: () => ({}),
})

const fullNameCompType = createCompNode({
	getDependencies: (component) => qaRefs.fullNameCompType(component.id),
	toViewItem: (__, data) => data,
})

const formatCssValue = createCompNode({
	getDependencies: () => {},
	toViewItem: () => resolveSpxAttribute,
})

const siteFontsSpxResolved = createCompNode({
	getDependencies: () => themeRefs.siteFonts,
	toViewItem: (__, fonts) =>
		fonts.reduce((acc, font, index) => {
			const fontObj = parseFontStr(font)
			const resolvedFontObj =
				fontObj.sizeUnit === 'spx'
					? { ...fontObj, size: `calc(${fontObj.size} * var(--theme-spx-ratio))`, sizeUnit: '' }
					: fontObj
			const fontString = fontObjectToString(resolvedFontObj, true)
			acc[`font_${index}`] = fontString
			return acc
		}, {} as ThemeFontMap),
})

const LEGACY_TRANSITION_TYPES_MAP: Record<string, PageTransition> = {
	outIn: 'OutIn',
	crossfade: 'CrossFade',
	shrinkfade: 'CrossFade',
	swipeHorizontal: 'SlideHorizontal',
	swipeHorizontalFullScreen: 'SlideHorizontal',
	swipeVertical: 'SlideVertical',
	swipeVerticalFullScreen: 'SlideVertical',
}

const pageTransition = createCompNode({
	getDependencies: (_component, refs) => ({
		isMobile: envRefs.isMobileView,
		sitePagesProperties: refs.propertyQuery<MasterPageDefinition['property']>('SITE_PAGES'),
		// TODO: Check if we can get the page list as a dependency
	}),
	toViewItem: (__, data): PageTransition => {
		const { isMobile, sitePagesProperties } = data
		// Should return none if we are in mobile view or we have only one page
		if (isMobile) {
			return 'none'
		}

		const transition = LEGACY_TRANSITION_TYPES_MAP[sitePagesProperties!.transition] || 'none'
		return transition as PageTransition
	},
})

const shouldShowFreemiumBanner = createCompNode({
	getDependencies: () => ({
		renderWixAdsOnNavigation: envRefs.shouldRenderWixAdsOnNavigation,
		isWixAdsAllowed: envRefs.isWixAdsAllowed,
	}),
	toViewItem: (__, data) => data.renderWixAdsOnNavigation && data.isWixAdsAllowed,
})

export const compInfoNodes = {
	animations,
	compData,
	compDesign,
	compId,
	compLayout,
	compProps,
	compStylableClass,
	customClassNames,
	deviceType,
	formFactor,
	direction,
	editorName,
	experiments,
	externalBaseUrl,
	formatCssValue,
	fullNameCompType,
	globalImageQuality,
	hasResponsiveLayout,
	isConnectedToDataBinding,
	isConsentPolicyActive,
	isInSeo,
	isMasterPage,
	isMobileView,
	isNonAdaptiveMobile,
	isQaMode,
	language,
	mainPageId,
	metaSiteId,
	originalComponentType: fullNameCompType,
	pageTransition,
	refApi,
	shouldShowFreemiumBanner,
	siteColors,
	siteFonts,
	siteFontsSpxResolved,
	siteWidth,
	skin,
	structure,
	styleProperties,
	topology,
	viewMode,
	isTrackClicksAnalyticsEnabled,
	compMeshProps: notImplemented,
	uiType: notImplemented,
	pagesMap: notImplemented,
	...(process.env.PACKAGE_NAME === 'thunderbolt-ds' && previewCompInfoNodes),
} as const

export type CompInfoDependencies = {
	[key in keyof CompInfo<any>]: ReturnType<typeof compInfoNodes[key]['getDependencies']>
}

export type CompInfoViewItems = {
	[key in keyof CompInfo<any>]: ReturnType<typeof compInfoNodes[key]['toViewItem']>
}
